import React, { useState, useEffect } from 'react';
import { Typography, Tooltip } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Text } = Typography;

const TimerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    gap: 4px;
  }
`;

const TimerText = styled(Text)`
  color: white !important;
  font-weight: 500;
  
  &.warning {
    color: #ffa940 !important;
  }
  
  &.danger {
    color: #ff4d4f !important;
    animation: blink 1s infinite;
  }
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

interface SessionTimerProps {
  expiryTime: number; // Unix timestamp in milliseconds
  onExpire?: () => void;
}

const SessionTimer: React.FC<SessionTimerProps> = ({ expiryTime, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = Date.now();
      const difference = expiryTime - now;

      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        if (onExpire) {
          onExpire();
        }
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    // Calculate immediately
    calculateTimeLeft();

    // Update every second
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [expiryTime, onExpire]);

  const formatTime = () => {
    const parts: string[] = [];
    
    if (timeLeft.days > 0) {
      parts.push(`${timeLeft.days}d`);
    }
    if (timeLeft.hours > 0 || timeLeft.days > 0) {
      parts.push(`${timeLeft.hours}h`);
    }
    if (timeLeft.minutes > 0 || timeLeft.hours > 0 || timeLeft.days > 0) {
      parts.push(`${timeLeft.minutes}m`);
    }
    parts.push(`${timeLeft.seconds}s`);

    return parts.join(' ');
  };

  const getTimerClass = () => {
    const totalSeconds = timeLeft.days * 86400 + timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds;
    if (totalSeconds <= 60) return 'danger';
    if (totalSeconds <= 300) return 'warning'; // 5 minutes
    return '';
  };

  const getExpiryDate = () => {
    const expiryDate = new Date(expiryTime);
    return expiryDate.toLocaleString();
  };

  return (
    <Tooltip title={`Session expires at: ${getExpiryDate()}`}>
      <TimerContainer>
        <ClockCircleOutlined />
        <TimerText className={getTimerClass()}>
          {formatTime()}
        </TimerText>
      </TimerContainer>
    </Tooltip>
  );
};

export default SessionTimer;

